import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import RestockHistoryDrawer from "@/components/RestockHistoryDrawer";
import MessageThread from "@/components/MessageThread";
import SellerProductEdit from "./SellerProductEdit";

const emptyProduct = {
  title: "", description: "", price: "", type: "physical",
  category: "art", image_url: "", images: [], digital_file_path: "",
  preview_url: "", stock: "", delivery_days: 15,
  free_shipping: false, shipping_price: "", allow_pickup: false, shipping_vat_included: false,
  event_dates: ["", "", ""], event_location: "", event_contact: "",
  apparel_section: "men", one_size: false, sizes: [], colors: [], variant_stock: {},
};

export default function SellerDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState({ sales: [], summary: { gross: 0, commission: 0, net: 0, commission_pct: 30 } });
  const [conversations, setConversations] = useState([]);
  const [openThread, setOpenThread] = useState(null);
  const [unread, setUnread] = useState(0);
  const [form, setForm] = useState(emptyProduct);
  const [submitting, setSubmitting] = useState(false);
  const [payout, setPayout] = useState({ method: "paypal", paypal_email: "", remit_phone: "", remit_country: "", bank_details: "" });
  const [stripeConnect, setStripeConnect] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [editingProductId, setEditingProductId] = useState(null);

  useEffect(() => {
    if (!loading) {
      if (!user) nav("/login");
      else if (user.role === "buyer") nav("/buyer");
      else if (user.role === "admin") nav("/admin");
    }
  }, [loading, user, nav]);

  const refresh = async () => {
    const [p, s] = await Promise.all([
      api.get("/seller/products"),
      api.get("/seller/sales"),
    ]);
    setProducts(p.data); setSales(s.data);
  };

  const refreshConvs = () => api.get("/conversations").then(({ data }) => {
    setConversations(data);
    setUnread(data.reduce((s, c) => s + (c.unread_count || 0), 0));
  }).catch(() => {});

  useEffect(() => {
    if (user && (user.role === "seller" || user.role === "admin")) {
      refresh();
      refreshConvs();
      const iv = setInterval(refreshConvs, 20000);
      if (user.payout) setPayout({ ...payout, ...user.payout });
      // Refresh Connect status; also detect return from Stripe onboarding
      const url = new URL(window.location.href);
      const ret = url.searchParams.get("stripe_return");
      if (ret) {
        setTab("payout");
        toast.success(ret === "ok" ? "Back from Stripe — refreshing status" : "Continue Stripe onboarding");
        url.searchParams.delete("stripe_return");
        window.history.replaceState({}, "", url.toString());
      }
      loadConnectStatus();
      return () => clearInterval(iv);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConnectStatus = async () => {
    try {
      const { data } = await api.get("/stripe/connect/status");
      setStripeConnect(data);
    } catch (e) {
      setStripeConnect({ connected: false });
    }
  };

  const startStripeOnboarding = async () => {
    setConnectLoading(true);
    try {
      const { data } = await api.post("/stripe/connect/onboard");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stripe onboarding failed");
    } finally {
      setConnectLoading(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const { data } = await api.post("/stripe/connect/login-link");
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open Stripe");
    }
  };

  const uploadImage = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    const url = `${BACKEND_URL}${data.url}`;
    setForm((f) => ({
      ...f,
      image_url: f.image_url || url,
      images: [...(f.images || []), url],
    }));
    toast.success("Image uploaded");
  };

  const removeImage = (url) => {
    setForm((f) => {
      const images = (f.images || []).filter((u) => u !== url);
      return { ...f, images, image_url: images[0] || "" };
    });
  };

  const uploadDigital = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/uploads/digital", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm((f) => ({ ...f, digital_file_path: data.path }));
    toast.success(`File uploaded: ${data.original_name}`);
  };

  const uploadPreview = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/uploads/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm((f) => ({ ...f, preview_url: `${BACKEND_URL}${data.url}` }));
    toast.success("Preview uploaded");
  };

  const restockProduct = async (p) => {
    if (p.category === "apparel") {
      const vs = p.variant_stock || {};
      const keys = Object.keys(vs);
      if (!keys.length) { toast.error("No variants to restock"); return; }
      const variants = {};
      for (const k of keys) {
        const label = k.replace("|", " / ");
        const add = parseInt(prompt(`Add stock to ${label} (current: ${vs[k]}). Enter amount to add (or 0 to skip):`, "0"));
        if (isNaN(add) || add < 0) return;
        if (add > 0) variants[k] = add;
      }
      if (!Object.keys(variants).length) return;
      await api.post(`/products/${p.product_id}/restock`, { variants });
      toast.success(`Restocked ${p.title}`);
    } else {
      const add = parseInt(prompt(`Add units to "${p.title}" (current stock: ${p.stock ?? "unlimited"}). Enter amount to add:`, "10"));
      if (isNaN(add) || add <= 0) return;
      await api.post(`/products/${p.product_id}/restock`, { amount: add });
      toast.success(`Added ${add} to ${p.title}`);
    }
    refresh();
  };

  const submitProduct = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const isTicket = form.category === "ticket";
      const isApparel = form.category === "apparel";
      const cleanDates = (form.event_dates || []).filter((d) => d && d.trim());
      if (isTicket && cleanDates.length === 0) {
        toast.error("Add at least one event date");
        setSubmitting(false); return;
      }
      let sizes = null;
      let variantStock = null;
      if (isApparel) {
        if (form.one_size) {
          sizes = ["ONE SIZE"];
        } else {
          sizes = (form.sizes || []).map((s) => s.trim()).filter(Boolean);
          if (sizes.length === 0) {
            toast.error("Add at least one size (or tick One size)");
            setSubmitting(false); return;
          }
        }
        // Build variant_stock from matrix: for each size × color combo
        variantStock = {};
        const colorList = (form.colors || []).filter((c) => c.name);
        const colorKeys = colorList.length ? colorList.map((c) => c.name) : [""];
        for (const s of sizes) {
          for (const ck of colorKeys) {
            const k = `${s}|${ck}`;
            const v = parseInt(form.variant_stock?.[k] || 0);
            variantStock[k] = isNaN(v) ? 0 : Math.max(0, v);
          }
        }
      }
      const payload = {
        ...form, price: parseFloat(form.price),
        stock: form.stock ? parseInt(form.stock) : null,
        digital_file_path: form.digital_file_path || null,
        preview_url: form.preview_url || null,
        images: form.images || [],
        delivery_days: form.type === "physical" ? parseInt(form.delivery_days) : null,
        free_shipping: form.type === "physical" ? !!form.free_shipping : false,
        shipping_price: form.type === "physical" && !form.free_shipping && form.shipping_price !== "" ? parseFloat(form.shipping_price) : null,
        allow_pickup: form.type === "physical" && !form.free_shipping ? !!form.allow_pickup : false,
        shipping_vat_included: form.type === "physical" && !form.free_shipping ? !!form.shipping_vat_included : false,
        event_dates: isTicket ? cleanDates : null,
        event_location: isTicket ? form.event_location : null,
        event_contact: isTicket ? form.event_contact : null,
        apparel_section: isApparel ? form.apparel_section : null,
        sizes: isApparel ? sizes : null,
        colors: isApparel ? (form.colors || []).filter((c) => c.name && c.image_url) : null,
        variant_stock: isApparel ? variantStock : null,
      };
      if (form.type === "physical" && !form.free_shipping) {
        if (form.shipping_price === "" || parseFloat(form.shipping_price) < 0) {
          toast.error("Enter a shipping price or tick free shipping");
          setSubmitting(false); return;
        }
      }
      delete payload.one_size;
      await api.post("/products", payload);
      toast.success("Submitted for admin review");
      setForm(emptyProduct); refresh();
      setTab("products");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  const savePayout = async () => {
    try {
      await api.post("/seller/payout", payout);
      toast.success("Payout details saved");
    } catch { toast.error("Failed to save"); }
  };

  if (!user) return null;

  return (
    <Layout>
      <section className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="mono text-xs uppercase tracking-[0.3em] text-[#C77E3C]">/ Seller</div>
        <h1 className="heading text-5xl font-black uppercase tracking-tight mt-2">{user.shop_name || user.name}</h1>

        {/* Commission callout */}
        <div className="mt-6 brut-border bg-[#C77E3C] text-white p-5 brut-shadow flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em]">Platform commission</div>
            <div className="heading text-3xl font-black uppercase mt-1">30% off every sale</div>
            <div className="text-sm mt-1 opacity-90">Jubasmash retains 30% of gross to cover hosting, curation & marketing. You keep 70%.</div>
          </div>
          <div className="grid grid-cols-3 gap-4 mono">
            <div><div className="text-[10px] uppercase tracking-[0.2em]">Gross</div><div className="heading text-2xl font-bold" data-testid="seller-gross">${sales.summary.gross.toFixed(2)}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.2em]">Commission</div><div className="heading text-2xl font-bold" data-testid="seller-commission">${sales.summary.commission.toFixed(2)}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.2em]">Net payout</div><div className="heading text-2xl font-bold" data-testid="seller-net">${sales.summary.net.toFixed(2)}</div></div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8 flex flex-wrap gap-0 brut-border w-fit">
          {[["products", "Products"], ["new", "Submit Product"], ["sales", "Sales"], ["messages", unread > 0 ? `Messages (${unread})` : "Messages"], ["payout", "Payout"]].map(([k, l], i) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => { setTab(k); if (k === "messages") api.get("/conversations").then(({ data }) => { setConversations(data); setUnread(data.reduce((s, c) => s + (c.unread_count || 0), 0)); }).catch(() => {}); }} className={`px-5 py-3 mono uppercase text-xs tracking-[0.25em] ${i > 0 ? "border-l-2 border-[#1F3D2A]" : ""} ${tab === k ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>{l}</button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "messages" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="seller-messages">
              <div className="brut-border bg-white">
                {conversations.length === 0 ? (
                  <div className="p-8 text-center text-[#555] mono uppercase text-xs tracking-[0.2em]">No conversations yet — buyers who chose local pickup can message you here.</div>
                ) : conversations.map((c, i) => (
                  <button key={c.conversation_id} data-testid={`seller-conv-${c.conversation_id}`} onClick={() => { setOpenThread(c.conversation_id); setTimeout(() => api.get("/conversations").then(({ data }) => { setConversations(data); setUnread(data.reduce((s, x) => s + (x.unread_count || 0), 0)); }).catch(() => {}), 800); }}
                    className={`w-full text-left p-4 hover:bg-[#FDF3E9] ${i > 0 ? "border-t-2 border-[#1F3D2A]" : ""} ${openThread === c.conversation_id ? "bg-[#FDF3E9]" : ""}`}>
                    <div className="flex items-center justify-between">
                      <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#C77E3C]">Order {c.order_id.slice(-10)}</div>
                      {c.unread_count > 0 && <span data-testid={`seller-unread-${c.conversation_id}`} className="brut-border bg-[#C77E3C] text-white px-2 py-0.5 mono text-[9px] uppercase tracking-[0.2em]">{c.unread_count} new</span>}
                    </div>
                    <div className="heading font-bold mt-1">{c.buyer_email}</div>
                    <div className="text-xs text-[#555] mt-1 truncate">{c.last_message_preview || "No messages yet"}</div>
                  </button>
                ))}
              </div>
              <div>
                {openThread ? (
                  <MessageThread conversationId={openThread} onClose={() => setOpenThread(null)} />
                ) : (
                  <div className="brut-border bg-white p-12 text-center mono text-xs uppercase tracking-[0.25em] text-[#555]">Pick a conversation to open →</div>
                )}
              </div>
            </div>
          )}

          {tab === "products" && (
            <div className="brut-border bg-white">
              {products.length === 0 ? (
                <div className="p-12 text-center text-[#555] mono uppercase text-xs tracking-[0.2em]">No products yet — submit one</div>
              ) : products.map((p, i) => (
                <div key={p.product_id} className={`flex items-center gap-4 p-4 ${i > 0 ? "border-t-2 border-[#1F3D2A]" : ""}`}>
                  <div className="w-16 h-16 brut-border bg-[#EAEAEA] overflow-hidden">{p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="flex-1">
                    <div className="heading font-bold">{p.title}</div>
                    <div className="mono text-xs text-[#555] uppercase tracking-[0.2em]">{p.type} · {p.category}</div>
                    {p.category === "ticket" && p.stock != null && (
                      <div className="mono text-[10px] uppercase tracking-[0.2em] mt-1" style={{ color: p.sold_out ? "#C77E3C" : "#1F3D2A" }}>
                        <strong>{p.tickets_sold || 0} / {p.stock}</strong> tickets sold{p.sold_out && " · SOLD OUT"}
                      </div>
                    )}
                    {p.category === "apparel" && p.variant_total_remaining != null && (
                      <div className="mono text-[10px] uppercase tracking-[0.2em] mt-1" style={{ color: p.sold_out ? "#C77E3C" : "#555" }}>
                        <strong>{p.variant_total_remaining}</strong> total across variants{p.sold_out && " · SOLD OUT"}
                      </div>
                    )}
                    {p.category !== "ticket" && p.category !== "apparel" && p.stock != null && (
                      <div className="mono text-[10px] uppercase tracking-[0.2em] mt-1" style={{ color: p.sold_out ? "#C77E3C" : "#555" }}>
                        <strong>{p.stock}</strong> in stock{p.sold_out && " · SOLD OUT"}
                      </div>
                    )}
                    {p.category !== "ticket" && p.category !== "apparel" && p.stock == null && (
                      <div className="mono text-[10px] uppercase tracking-[0.2em] mt-1 text-[#555]">Unlimited stock</div>
                    )}
                  </div>
                  <div className="mono font-bold" data-testid={`seller-price-${p.product_id}`}>
                    {p.price === 0 ? <span className="text-[#C77E3C]">FREE</span> : `$${p.price.toFixed(2)}`}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button data-testid={`seller-edit-${p.product_id}`} onClick={() => setEditingProductId(p.product_id)} className="brut-border bg-white px-3 py-1 mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C77E3C] hover:text-white">Edit</button>
                    {p.status === "approved" && (
                      <>
                        <button data-testid={`restock-${p.product_id}`} onClick={() => restockProduct(p)} className="brut-border bg-white px-3 py-1 mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#1F3D2A] hover:text-white">Restock +</button>
                        <button data-testid={`restock-history-${p.product_id}`} onClick={() => setHistoryProduct(p)} className="brut-border bg-white px-3 py-1 mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C77E3C] hover:text-white">History</button>
                      </>
                    )}
                  </div>
                  <span className={`pill-label ${p.status === "approved" ? "bg-[#C77E3C]" : p.status === "rejected" ? "bg-[#555]" : "bg-[#1F3D2A]"}`}>{p.status}</span>
                </div>
              ))}
            </div>
          )}

          {tab === "new" && (
            <form onSubmit={submitProduct} className="brut-border bg-white p-6 max-w-3xl space-y-4">
              <h3 className="heading text-2xl font-bold uppercase">Submit a product</h3>
              <p className="text-sm text-[#555]">All submissions are reviewed by Jubasmash admin before going live.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Title</label>
                  <input data-testid="prod-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" />
                </div>
                <div className="md:col-span-2">
                  <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Description</label>
                  <textarea data-testid="prod-desc" required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" />
                </div>
                <div>
                  <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Price (USD)</label>
                  <input data-testid="prod-price" required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" />
                </div>
                <div>
                  <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Type</label>
                  <select data-testid="prod-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3">
                    <option value="physical">Physical</option>
                    <option value="digital">Digital</option>
                  </select>
                </div>
                <div>
                  <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Category</label>
                  <select data-testid="prod-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3">
                    <option value="book">Book</option>
                    <option value="music">Music</option>
                    <option value="art">Object / Art</option>
                    <option value="apparel">Apparel</option>
                    <option value="ticket">Ticket / Event</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                {form.category === "apparel" && (
                  <div className="md:col-span-2 brut-border bg-[#FDF3E9] p-5 space-y-5">
                    <div>
                      <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Apparel details</div>
                      <div className="heading text-lg font-bold uppercase mt-1">Section, sizes & colours</div>
                    </div>
                    <div>
                      <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Section *</label>
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 brut-border">
                        {["men", "women", "children", "accessories"].map((sec, i) => (
                          <button key={sec} type="button" data-testid={`section-${sec}`}
                            onClick={() => setForm({ ...form, apparel_section: sec })}
                            className={`py-3 mono uppercase text-xs tracking-[0.2em] ${i > 0 ? "border-l-2 border-[#1F3D2A]" : ""} ${form.apparel_section === sec ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>
                            {sec}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="flex items-center gap-2 brut-border bg-white px-4 py-3 cursor-pointer w-fit">
                        <input data-testid="one-size" type="checkbox" checked={form.one_size} onChange={(e) => setForm({ ...form, one_size: e.target.checked })} />
                        <span className="mono text-xs uppercase tracking-[0.2em]">One size</span>
                      </label>
                    </div>
                    {!form.one_size && (
                      <div>
                        <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Sizes (up to 20)</label>
                        <div className="mt-2 flex flex-wrap gap-2 mb-2">
                          {["XS", "S", "M", "L", "XL", "XXL", "3XL"].map((s) => (
                            <button key={s} type="button" data-testid={`size-preset-${s}`}
                              onClick={() => {
                                if ((form.sizes || []).includes(s)) return;
                                if ((form.sizes || []).length >= 20) return;
                                setForm({ ...form, sizes: [...(form.sizes || []), s] });
                              }}
                              className="brut-border bg-white px-3 py-1 mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#1F3D2A] hover:text-white">
                              + {s}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input data-testid="size-input" placeholder="Custom size (e.g. 42, US 8, 36 EU)" id="_sz" className="flex-1 brut-border bg-white px-3 py-2 text-sm" />
                          <button type="button" onClick={() => {
                            const inp = document.getElementById("_sz");
                            const v = inp.value.trim();
                            if (!v) return;
                            if ((form.sizes || []).length >= 20) { toast.error("Max 20 sizes"); return; }
                            if ((form.sizes || []).includes(v)) return;
                            setForm({ ...form, sizes: [...(form.sizes || []), v] });
                            inp.value = "";
                          }} className="brut-border bg-[#1F3D2A] text-white px-4 mono text-xs uppercase tracking-[0.2em]">Add</button>
                        </div>
                        {(form.sizes || []).length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2" data-testid="sizes-list">
                            {form.sizes.map((s, i) => (
                              <span key={i} className="brut-border bg-[#1F3D2A] text-white pl-3 pr-1 py-1 mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2">
                                {s}
                                <button type="button" onClick={() => setForm({ ...form, sizes: form.sizes.filter((_, j) => j !== i) })} className="hover:text-[#C77E3C] px-2">✕</button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div>
                      <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Colours (up to 10) — each with its own photo</label>
                      <div className="mt-3 space-y-2" data-testid="colors-list">
                        {(form.colors || []).map((c, idx) => (
                          <div key={idx} className="flex items-center gap-3 brut-border bg-white p-2">
                            <div className="w-14 h-14 brut-border bg-[#EAEAEA] overflow-hidden flex-shrink-0">
                              {c.image_url && <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" />}
                            </div>
                            <input placeholder="Colour name (e.g. Rust)" value={c.name} onChange={(e) => {
                              const arr = [...form.colors]; arr[idx] = { ...arr[idx], name: e.target.value }; setForm({ ...form, colors: arr });
                            }} className="flex-1 brut-border bg-white px-3 py-2 text-sm" />
                            <label className="brut-border bg-white px-3 py-2 mono text-[10px] uppercase tracking-[0.2em] cursor-pointer hover:bg-[#1F3D2A] hover:text-white">
                              {c.image_url ? "Change" : "Upload"}
                              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                const f = e.target.files?.[0]; if (!f) return;
                                const fd = new FormData(); fd.append("file", f);
                                const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                const arr = [...form.colors]; arr[idx] = { ...arr[idx], image_url: `${BACKEND_URL}${data.url}` }; setForm({ ...form, colors: arr });
                              }} />
                            </label>
                            <button type="button" onClick={() => setForm({ ...form, colors: form.colors.filter((_, j) => j !== idx) })} className="brut-border bg-white px-3 py-2 mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C77E3C] hover:text-white">✕</button>
                          </div>
                        ))}
                      </div>
                      <button type="button" data-testid="add-color-btn" onClick={() => {
                        if ((form.colors || []).length >= 10) { toast.error("Max 10 colours"); return; }
                        setForm({ ...form, colors: [...(form.colors || []), { name: "", image_url: "" }] });
                      }} className="mt-3 brut-border bg-white px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-[#1F3D2A] hover:text-white">+ Add colour</button>
                    </div>
                    {/* Stock matrix */}
                    {((form.one_size ? ["ONE SIZE"] : (form.sizes || [])).length > 0) && (
                      <div>
                        <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Stock per variant</label>
                        <p className="text-xs text-[#555] mt-1 mb-2">How many units of each size × colour combination. Set 0 for combos you don&apos;t stock.</p>
                        <div className="overflow-x-auto brut-border bg-white">
                          <table className="w-full text-sm">
                            <thead className="bg-[#1F3D2A] text-white">
                              <tr className="mono text-[10px] uppercase tracking-[0.2em]">
                                <th className="p-2 text-left">Size ↓ / Colour →</th>
                                {((form.colors || []).filter((c) => c.name).length ? form.colors.filter((c) => c.name).map((c) => c.name) : ["—"]).map((cn) => (
                                  <th key={cn} className="p-2 text-center">{cn}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {(form.one_size ? ["ONE SIZE"] : form.sizes).map((s) => {
                                const cols = (form.colors || []).filter((c) => c.name).length ? form.colors.filter((c) => c.name).map((c) => c.name) : [""];
                                return (
                                  <tr key={s} className="border-t-2 border-[#1F3D2A]">
                                    <td className="p-2 mono uppercase text-xs font-bold">{s}</td>
                                    {cols.map((ck) => {
                                      const key = `${s}|${ck}`;
                                      return (
                                        <td key={key} className="p-2">
                                          <input data-testid={`vstock-${key}`} type="number" min="0" value={form.variant_stock?.[key] ?? 0}
                                            onChange={(e) => setForm({ ...form, variant_stock: { ...(form.variant_stock || {}), [key]: parseInt(e.target.value) || 0 } })}
                                            className="w-20 brut-border bg-white px-2 py-1 text-center mono" />
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {form.category === "ticket" && (
                  <div className="md:col-span-2 brut-border bg-[#FDF3E9] p-5 space-y-4">
                    <div>
                      <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Event details</div>
                      <div className="heading text-lg font-bold uppercase mt-1">Add up to 3 possible dates — buyer picks one at checkout</div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[0, 1, 2].map((idx) => (
                        <div key={idx}>
                          <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">Date {idx + 1}{idx === 0 ? " *" : ""}</label>
                          <input data-testid={`event-date-${idx}`} type="date" value={form.event_dates[idx] || ""}
                            onChange={(e) => {
                              const arr = [...form.event_dates];
                              arr[idx] = e.target.value;
                              setForm({ ...form, event_dates: arr });
                            }}
                            className="w-full mt-2 brut-border bg-white px-3 py-2 text-sm" />
                        </div>
                      ))}
                    </div>
                    <div>
                      <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Location *</label>
                      <input data-testid="event-location" value={form.event_location} onChange={(e) => setForm({ ...form, event_location: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" placeholder="Venue name, city" />
                    </div>
                    <div>
                      <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Contact (email or phone) *</label>
                      <input data-testid="event-contact" value={form.event_contact} onChange={(e) => setForm({ ...form, event_contact: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" placeholder="organiser@email.com or +33..." />
                    </div>
                    <div>
                      <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Number of tickets to sell *</label>
                      <input data-testid="event-capacity" type="number" min="1" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" placeholder="e.g. 50" />
                      <p className="text-xs text-[#555] mt-2">Total capacity. Event will be marked <strong>SOLD OUT</strong> once every ticket is bought.</p>
                    </div>
                  </div>
                )}
                {form.type === "physical" && (
                  <div className="md:col-span-2 brut-border bg-[#FDF3E9] p-5 space-y-4">
                    <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Shipping</div>
                    <label className="flex items-center gap-2 brut-border bg-white px-4 py-3 cursor-pointer w-fit">
                      <input data-testid="prod-free-shipping" type="checkbox" checked={form.free_shipping}
                        onChange={(e) => setForm({ ...form, free_shipping: e.target.checked, allow_pickup: false })} />
                      <span className="mono text-xs uppercase tracking-[0.2em]">Free shipping (I absorb the cost)</span>
                    </label>
                    {!form.free_shipping && (
                      <>
                        <div>
                          <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Shipping price (USD)</label>
                          <input data-testid="prod-shipping-price" required type="number" step="0.01" min="0" value={form.shipping_price}
                            onChange={(e) => setForm({ ...form, shipping_price: e.target.value })}
                            className="w-full mt-2 brut-border bg-white px-4 py-3" placeholder="e.g. 5.99 — added to buyer's total at checkout" />
                        </div>
                        <label className="flex items-center gap-2 brut-border bg-white px-4 py-3 cursor-pointer w-fit">
                          <input data-testid="prod-allow-pickup" type="checkbox" checked={form.allow_pickup}
                            onChange={(e) => setForm({ ...form, allow_pickup: e.target.checked })} />
                          <span className="mono text-xs uppercase tracking-[0.2em]">Also offer local pickup (buyer collects — free)</span>
                        </label>
                        {form.allow_pickup && (
                          <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555] pl-2">
                            Pickup location shown to buyer: <strong>{user?.address?.city || "(no city)"}, {user?.address?.country || "(no country)"}</strong>
                            {(!user?.address?.city || !user?.address?.country) && (
                              <span className="text-[#C77E3C]"> — set your address in Account first</span>
                            )}
                          </div>
                        )}
                        {form.type === "physical" && !form.free_shipping && (
                          <label className="flex items-center gap-2 brut-border bg-white px-4 py-3 cursor-pointer w-fit mt-2">
                            <input data-testid="prod-vat-included" type="checkbox" checked={form.shipping_vat_included}
                              onChange={(e) => setForm({ ...form, shipping_vat_included: e.target.checked })} />
                            <span className="mono text-xs uppercase tracking-[0.2em]">Shipping price already includes VAT / tax</span>
                          </label>
                        )}
                      </>
                    )}
                  </div>
                )}
                {form.type === "physical" && (
                  <div>
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Stock</label>
                    <input data-testid="prod-stock" type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" />
                  </div>
                )}
                {form.type === "physical" && (
                  <div>
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Delivery window</label>
                    <div className="mt-2 grid grid-cols-2 brut-border">
                      {[15, 30].map((d, i) => (
                        <button key={d} type="button" data-testid={`delivery-${d}`}
                          onClick={() => setForm({ ...form, delivery_days: d })}
                          className={`py-3 mono uppercase text-xs tracking-[0.25em] ${i > 0 ? "border-l-2 border-[#1F3D2A]" : ""} ${form.delivery_days === d ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>
                          {d} days
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Cover image(s) — first one is the main image</label>
                  <input data-testid="prod-image-upload" type="file" accept="image/*" multiple
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      for (const f of files) await uploadImage(f);
                      e.target.value = "";
                    }}
                    className="w-full mt-2 brut-border bg-white px-4 py-3" />
                  {form.images && form.images.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="prod-image-thumbs">
                      {form.images.map((url, idx) => (
                        <div key={idx} className="relative w-24 h-24 brut-border overflow-hidden group">
                          <img src={url} alt="" className="w-full h-full object-cover" />
                          {idx === 0 && <span className="absolute top-0 left-0 bg-[#C77E3C] text-white mono text-[8px] px-1 uppercase">Main</span>}
                          <button type="button" onClick={() => removeImage(url)}
                            className="absolute bottom-0 right-0 bg-[#1F3D2A] text-white mono text-[10px] px-1 opacity-0 group-hover:opacity-100">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {form.type === "digital" && (
                  <div className="md:col-span-2">
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Digital file (PDF, MP3, ZIP)</label>
                    <input data-testid="prod-digital-upload" type="file" onChange={(e) => e.target.files?.[0] && uploadDigital(e.target.files[0])} className="w-full mt-2 brut-border bg-white px-4 py-3" />
                    {form.digital_file_path && <div className="mt-2 mono text-xs text-[#555]">Uploaded: {form.digital_file_path}</div>}
                  </div>
                )}
                {(form.category === "music" || form.category === "book") && (
                  <div className="md:col-span-2">
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">
                      {form.category === "music" ? "Audio preview (MP3 sample)" : "Sample PDF (first few pages)"}
                    </label>
                    <input data-testid="prod-preview-upload" type="file"
                      accept={form.category === "music" ? "audio/*" : "application/pdf"}
                      onChange={(e) => e.target.files?.[0] && uploadPreview(e.target.files[0])}
                      className="w-full mt-2 brut-border bg-white px-4 py-3" />
                    {form.preview_url && <div className="mt-2 mono text-xs text-[#555]">Preview uploaded ✓</div>}
                  </div>
                )}
              </div>
              <button data-testid="submit-product-btn" disabled={submitting} className="btn-primary">{submitting ? "Submitting…" : "Submit for review"}</button>
            </form>
          )}

          {tab === "sales" && (
            <div className="brut-border bg-white overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1F3D2A] text-white">
                  <tr className="mono text-[10px] uppercase tracking-[0.25em]">
                    <th className="text-left p-3">Date</th><th className="text-left p-3">Product</th>
                    <th className="text-left p-3">Buyer</th><th className="text-right p-3">Qty</th>
                    <th className="text-right p-3">Gross</th><th className="text-right p-3">Commission</th>
                    <th className="text-right p-3">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.sales.length === 0 ? (
                    <tr><td colSpan={7} className="p-12 text-center mono text-xs uppercase tracking-[0.2em] text-[#555]">No sales yet</td></tr>
                  ) : sales.sales.map((s, i) => (
                    <tr key={i} className="border-t-2 border-[#1F3D2A]">
                      <td className="p-3 text-sm">{new Date(s.date).toLocaleDateString()}</td>
                      <td className="p-3 text-sm font-bold">{s.title}</td>
                      <td className="p-3 text-sm">{s.buyer_email}</td>
                      <td className="p-3 text-sm mono text-right">{s.qty}</td>
                      <td className="p-3 text-sm mono text-right">${s.gross.toFixed(2)}</td>
                      <td className="p-3 text-sm mono text-right text-[#C77E3C]">-${s.commission.toFixed(2)}</td>
                      <td className="p-3 text-sm mono text-right font-bold">${s.net.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "payout" && (
            <div className="brut-border bg-white p-6 max-w-2xl space-y-6">
              {/* Stripe Connect section (optional / advanced) */}
              <div className="space-y-3 pb-6 border-b-2 border-[#1F3D2A]">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#555]">/ Optional · Advanced</div>
                    <h3 className="heading text-2xl font-bold uppercase mt-1">Stripe Connect (Automated Payouts)</h3>
                  </div>
                  {stripeConnect?.payouts_enabled && stripeConnect?.charges_enabled ? (
                    <div className="mono text-xs uppercase tracking-[0.2em] bg-[#1F3D2A] text-white px-3 py-2" data-testid="stripe-status-active">Active</div>
                  ) : stripeConnect?.account_id ? (
                    <div className="mono text-xs uppercase tracking-[0.2em] bg-[#C77E3C] text-white px-3 py-2" data-testid="stripe-status-pending">Pending onboarding</div>
                  ) : (
                    <div className="mono text-xs uppercase tracking-[0.2em] bg-[#EAEAEA] text-[#1F3D2A] px-3 py-2" data-testid="stripe-status-none">Not connected</div>
                  )}
                </div>
                <p className="text-sm text-[#555]">Once enabled by the platform, connecting Stripe lets you receive your 70% share automatically after every sale. Until then, you&apos;ll be paid manually via the details below.</p>
                {stripeConnect?.account_id && (
                  <div className="grid grid-cols-2 gap-3 mono text-xs">
                    <div className={`brut-border p-3 ${stripeConnect.charges_enabled ? "bg-[#F0F5F1]" : "bg-[#FDF3E9]"}`}>
                      <div className="uppercase tracking-[0.2em] text-[10px] text-[#555]">Charges</div>
                      <div className="font-bold mt-1">{stripeConnect.charges_enabled ? "Enabled" : "Pending"}</div>
                    </div>
                    <div className={`brut-border p-3 ${stripeConnect.payouts_enabled ? "bg-[#F0F5F1]" : "bg-[#FDF3E9]"}`}>
                      <div className="uppercase tracking-[0.2em] text-[10px] text-[#555]">Payouts</div>
                      <div className="font-bold mt-1">{stripeConnect.payouts_enabled ? "Enabled" : "Pending"}</div>
                    </div>
                  </div>
                )}
                <div className="flex gap-3 flex-wrap">
                  <button data-testid="stripe-connect-btn" onClick={startStripeOnboarding} disabled={connectLoading} className="brut-border bg-white px-4 py-3 mono text-xs uppercase tracking-[0.2em] hover:bg-[#1F3D2A] hover:text-white">
                    {connectLoading ? "Redirecting…" : stripeConnect?.account_id ? (stripeConnect.details_submitted ? "Update Stripe details" : "Continue Stripe onboarding") : "Connect Stripe (when available)"}
                  </button>
                  {stripeConnect?.account_id && (
                    <>
                      <button data-testid="stripe-refresh-btn" onClick={loadConnectStatus} className="brut-border bg-white px-4 py-3 mono text-xs uppercase tracking-[0.2em]">Refresh status</button>
                      {stripeConnect.charges_enabled && (
                        <button data-testid="stripe-dashboard-btn" onClick={openStripeDashboard} className="brut-border bg-white px-4 py-3 mono text-xs uppercase tracking-[0.2em]">Stripe dashboard →</button>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Primary</div>
                  <h3 className="heading text-2xl font-bold uppercase mt-1">Payout details</h3>
                </div>
              </div>
              <p className="text-sm text-[#555]">How Jubasmash pays you your 70% net share. Payouts are handled manually by admin on a regular cadence.</p>
              <div>
                <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Method</label>
                <select data-testid="payout-method" value={payout.method} onChange={(e) => setPayout({ ...payout, method: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3">
                  <option value="paypal">PayPal</option>
                  <option value="remit">Remit / Mobile money</option>
                  <option value="bank">Bank transfer</option>
                </select>
              </div>
              {payout.method === "paypal" && (
                <div>
                  <label className="mono text-xs uppercase tracking-[0.2em] font-bold">PayPal email</label>
                  <input data-testid="payout-paypal" type="email" value={payout.paypal_email} onChange={(e) => setPayout({ ...payout, paypal_email: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" />
                </div>
              )}
              {payout.method === "remit" && (
                <>
                  <div>
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Phone (mobile money)</label>
                    <input data-testid="payout-phone" value={payout.remit_phone} onChange={(e) => setPayout({ ...payout, remit_phone: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" />
                  </div>
                  <div>
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Country</label>
                    <input data-testid="payout-country" value={payout.remit_country} onChange={(e) => setPayout({ ...payout, remit_country: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" />
                  </div>
                </>
              )}
              {payout.method === "bank" && (
                <div>
                  <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Bank details (free text)</label>
                  <textarea data-testid="payout-bank" rows={3} value={payout.bank_details} onChange={(e) => setPayout({ ...payout, bank_details: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" />
                </div>
              )}
              <button data-testid="save-payout-btn" onClick={savePayout} className="btn-primary">Save payout details</button>
            </div>
          )}
        </div>
      </section>
      <RestockHistoryDrawer product={historyProduct} onClose={() => setHistoryProduct(null)} />
      {editingProductId && (
        <SellerProductEdit
          productId={editingProductId}
          onClose={() => setEditingProductId(null)}
          onSaved={() => { setEditingProductId(null); refresh(); }}
        />
      )}
    </Layout>
  );
}
